/**
 * Create or edit a model in the D1 catalogue. The four record parts are edited as JSON
 * (shapes of model-registry.js, model-pricing.js, model-audit.js); admin-service checks
 * the whole record against the catalogue gates on save and returns every problem.
 */
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  AdminApiError,
  createCatalogueModel,
  updateCatalogueModel,
  type ModelCatalogueSchema,
  type ModelRow,
} from '../../utils/adminApi';
import { FocusManager } from '../ui/FocusManager';

type Part = 'config' | 'pricing' | 'licensing' | 'caveats';

const PARTS: Array<{ part: Part; label: string; help: string }> = [
  {
    part: 'config',
    label: 'Registry entry',
    help: 'The model-registry.js entry. `id` is permanent once created.',
  },
  {
    part: 'pricing',
    label: 'Provider cost',
    help: 'What the provider charges us, in micro-USD (model-pricing.js).',
  },
  {
    part: 'licensing',
    label: 'Licence verdict',
    help: 'A new model must arrive verified (CLAUDE-DOC:MODEL_LICENSING).',
  },
  {
    part: 'caveats',
    label: 'Caveats',
    help: 'Strong doc caveats, each enforced or surfaced (CLAUDE-DOC:MODEL_CAVEATS).',
  },
];

/** A licence verdict with every required field present but unfilled. */
const BLANK_LICENSING = {
  status: 'verified',
  checkedOn: '',
  terms: [],
  verdict: '',
  ticket: '',
};

const pretty = (value: unknown) => JSON.stringify(value, null, 2);

/**
 * The parts a group or 'Start from' change replaces; licensing and caveats typed so far
 * stay (a licence is never copied and depends on no group).
 */
const TEMPLATE_PARTS: Part[] = ['config', 'pricing'];

/**
 * Starting text for a new model copied from `source`: its registry entry and pricing
 * shape with the id cleared and a blank licence verdict (never inherited).
 */
function textsFromSource(source: ModelRow | undefined): Record<Part, string> {
  if (!source) {
    return {
      config: pretty({ id: '', name: '', category: '' }),
      pricing: pretty({ modelId: '', provider: '', pricingModel: '' }),
      licensing: pretty(BLANK_LICENSING),
      caveats: '[]',
    };
  }
  return {
    config: pretty({ ...source.config, id: '', name: '' }),
    pricing: pretty({ ...(source.pricing ?? {}), modelId: '' }),
    licensing: pretty(BLANK_LICENSING),
    caveats: '[]',
  };
}

function textsFromModel(model: ModelRow): Record<Part, string> {
  return {
    config: pretty(model.config),
    pricing: pretty(model.pricing),
    licensing: pretty(model.audit.licensing),
    caveats: pretty(model.audit.caveats),
  };
}

interface ModelCatalogueEditorProps {
  idToken: string;
  /** The model being edited; omitted to create one. */
  model?: ModelRow;
  models: ModelRow[];
  schema: ModelCatalogueSchema;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

export const ModelCatalogueEditor: React.FC<ModelCatalogueEditorProps> = ({
  idToken,
  model,
  models,
  schema,
  onClose,
  onSaved,
}) => {
  const isNew = !model;
  const fieldId = useId();
  const [registryGroup, setRegistryGroup] = useState(
    model?.registryGroup ?? schema.groups[0]?.group ?? ''
  );
  // Texts last loaded from the saved model or a template; edits are measured against
  // them (applying a template isn't an edit).
  const [loadedTexts, setLoadedTexts] = useState<Record<Part, string>>(() =>
    model ? textsFromModel(model) : textsFromSource(undefined)
  );
  const [texts, setTexts] = useState(loadedTexts);
  const [sourceId, setSourceId] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const groupCategories = useMemo(
    () =>
      schema.groups.find((group) => group.group === registryGroup)
        ?.categories ?? [],
    [schema, registryGroup]
  );
  const copyCandidates = models.filter(
    (candidate) =>
      candidate.isCatalogue && candidate.registryGroup === registryGroup
  );

  const isEdited = (part: Part) => texts[part] !== loadedTexts[part];
  const dirty = PARTS.some(({ part }) => isEdited(part));

  /**
   * Load `parts` of a template, asking first when that would replace edits.
   * Returns false when the admin keeps their edits instead.
   */
  const applyTemplate = (
    template: Record<Part, string>,
    parts: Part[]
  ): boolean => {
    if (
      parts.some(isEdited) &&
      !window.confirm('Replace your edits with this template?')
    ) {
      return false;
    }
    const next = { ...texts };
    const nextLoaded = { ...loadedTexts };
    for (const part of parts) {
      next[part] = template[part];
      nextLoaded[part] = template[part];
    }
    setTexts(next);
    setLoadedTexts(nextLoaded);
    return true;
  };

  /** Close, asking first when there are edits that would be lost. */
  const requestClose = useCallback(() => {
    if (saving) return;
    if (
      dirty &&
      !window.confirm('Discard your unsaved changes to this model?')
    ) {
      return;
    }
    onClose();
  }, [dirty, onClose, saving]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape inside a field belongs to the field (closing autocomplete).
      const target = event.target as HTMLElement | null;
      if (target?.closest('textarea, input, select')) return;
      requestClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [requestClose]);

  const parsed = useMemo(() => {
    const values: Partial<Record<Part, unknown>> = {};
    const errors: Partial<Record<Part, string>> = {};
    for (const { part } of PARTS) {
      try {
        values[part] = JSON.parse(texts[part]);
      } catch (error) {
        errors[part] = error instanceof Error ? error.message : 'Invalid JSON';
      }
    }
    return { values, errors };
  }, [texts]);

  const hasParseErrors = Object.keys(parsed.errors).length > 0;

  const handleSave = async () => {
    if (hasParseErrors || saving) return;
    setSaving(true);
    setProblems([]);
    const { config, pricing, licensing, caveats } = parsed.values as {
      config: ModelRow['config'];
      pricing: NonNullable<ModelRow['pricing']>;
      licensing: NonNullable<ModelRow['audit']['licensing']>;
      caveats: ModelRow['audit']['caveats'];
    };
    try {
      if (isNew) {
        await createCatalogueModel(idToken, {
          registryGroup,
          config,
          pricing,
          licensing,
          caveats,
        });
      } else {
        await updateCatalogueModel(idToken, model.id, {
          config,
          pricing,
          licensing,
          caveats,
          expectedCatalogVersion: model.catalogVersion ?? 0,
        });
      }
      await onSaved();
      onClose();
    } catch (error) {
      if (error instanceof AdminApiError && error.problems.length > 0) {
        setProblems(error.problems);
      } else {
        setProblems([
          error instanceof Error ? error.message : 'Failed to save the model',
        ]);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 overflow-y-auto py-8"
      onClick={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <FocusManager className="w-full max-w-5xl mx-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="model-editor-title"
          className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full space-y-4"
        >
          <div className="flex items-center justify-between">
            <h2
              id="model-editor-title"
              className="text-xl font-semibold text-gray-100"
            >
              {isNew ? 'Add model' : `Edit ${model.name}`}
              {!isNew && (
                <span className="ml-2 text-sm font-mono text-gray-500">
                  {model.id}
                </span>
              )}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              disabled={saving}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-100 text-xl"
            >
              ×
            </button>
          </div>

          {isNew && (
            <p className="text-sm text-gray-400">
              A new model must run on an input profile the deployed workers
              already have. It is saved disabled — enable it from the table once
              you have tried it.
            </p>
          )}

          <div className="flex flex-wrap gap-4">
            <div className="text-sm text-gray-300">
              <label htmlFor={`${fieldId}-group`}>Registry group</label>
              <select
                id={`${fieldId}-group`}
                value={registryGroup}
                onChange={(event) => {
                  if (
                    !applyTemplate(textsFromSource(undefined), TEMPLATE_PARTS)
                  ) {
                    return;
                  }
                  setRegistryGroup(event.target.value);
                  setSourceId('');
                }}
                disabled={!isNew || saving}
                className="block mt-1 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-gray-100"
              >
                {schema.groups.map(({ group }) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
            </div>
            {isNew && (
              <div className="text-sm text-gray-300">
                <label htmlFor={`${fieldId}-source`}>Start from</label>
                <select
                  id={`${fieldId}-source`}
                  value={sourceId}
                  onChange={(event) => {
                    const source = copyCandidates.find(
                      (candidate) => candidate.id === event.target.value
                    );
                    if (
                      !applyTemplate(textsFromSource(source), TEMPLATE_PARTS)
                    ) {
                      return;
                    }
                    setSourceId(event.target.value);
                  }}
                  disabled={saving}
                  className="block mt-1 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-gray-100"
                >
                  <option value="">Blank</option>
                  {copyCandidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      Copy of {candidate.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <details className="text-xs text-gray-400 bg-gray-900/60 border border-gray-700 rounded p-3">
            <summary className="cursor-pointer text-gray-300">
              What the deployed workers support
            </summary>
            <dl className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
              <dt className="text-gray-300">category ({registryGroup})</dt>
              <dd className="font-mono">{groupCategories.join(', ')}</dd>
              {Object.entries(schema.inputProfiles).map(([kind, profiles]) => (
                <React.Fragment key={kind}>
                  <dt className="text-gray-300">
                    replicateInputProfile ({kind})
                  </dt>
                  <dd className="font-mono">{profiles.join(', ')}</dd>
                </React.Fragment>
              ))}
              <dt className="text-gray-300">optionGroups</dt>
              <dd className="font-mono">{schema.optionGroups.join(', ')}</dd>
              <dt className="text-gray-300">cutoutPreparation (3D)</dt>
              <dd className="font-mono">
                {schema.cutoutPreparations.join(', ')}
              </dd>
              <dt className="text-gray-300">etaTier</dt>
              <dd className="font-mono">{schema.etaTiers.join(', ')}</dd>
              <dt className="text-gray-300">regionRestriction</dt>
              <dd className="font-mono">
                {schema.regionRestrictions.join(', ')}
              </dd>
              {Object.entries(schema.pricingModels).map(
                ([pricingModel, fields]) => (
                  <React.Fragment key={pricingModel}>
                    <dt className="text-gray-300">
                      pricingModel {pricingModel}
                    </dt>
                    <dd className="font-mono">{fields.join(', ')}</dd>
                  </React.Fragment>
                )
              )}
            </dl>
          </details>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {PARTS.map(({ part, label, help }) => (
              <div key={part} className="block text-sm text-gray-300">
                <label htmlFor={`${fieldId}-${part}`} className="font-semibold">
                  {label}
                </label>
                <span
                  id={`${fieldId}-${part}-help`}
                  className="block text-xs text-gray-500"
                >
                  {help}
                </span>
                <textarea
                  id={`${fieldId}-${part}`}
                  aria-describedby={
                    parsed.errors[part]
                      ? `${fieldId}-${part}-help ${fieldId}-${part}-error`
                      : `${fieldId}-${part}-help`
                  }
                  value={texts[part]}
                  onChange={(event) =>
                    setTexts((current) => ({
                      ...current,
                      [part]: event.target.value,
                    }))
                  }
                  disabled={saving}
                  spellCheck={false}
                  rows={part === 'config' ? 18 : 10}
                  aria-invalid={Boolean(parsed.errors[part])}
                  className="mt-1 w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 font-mono text-xs text-gray-100"
                />
                {parsed.errors[part] && (
                  <span
                    id={`${fieldId}-${part}-error`}
                    className="block text-xs text-red-300"
                  >
                    {parsed.errors[part]}
                  </span>
                )}
              </div>
            ))}
          </div>

          {problems.length > 0 && (
            <div
              role="alert"
              className="bg-red-900/40 border border-red-700 text-red-200 px-4 py-3 rounded text-sm"
            >
              <div className="font-semibold mb-1">Not saved:</div>
              <ul className="list-disc ml-5 space-y-0.5">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={requestClose}
              disabled={saving}
              className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-gray-100 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || hasParseErrors}
              className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-semibold"
            >
              {saving ? 'Saving…' : isNew ? 'Add model' : 'Save'}
            </button>
          </div>
        </div>
      </FocusManager>
    </div>
  );
};
